import { Link } from "react-router-dom";
import {
  ArrowRight, Compass, Wrench, ShieldCheck, Crown, Zap,
  User, Briefcase, HeartHandshake, Church, Rocket, Users,
  Building2, TrendingUp, FileText, Award, CalendarCheck, Layers, Handshake,
  CheckCircle2, ArrowDown,
} from "lucide-react";
import { SITE, SERVICES, FRAMEWORK_LEVELS, WHO_WE_SERVE, WHY_TRJ, CLIENT_JOURNEY } from "@/lib/site";
import { ScheduleConsultationButton } from "@/components/site/PageHero";

const ICONS = {
  Compass, Wrench, ShieldCheck, Crown, Zap,
  User, Briefcase, HeartHandshake, Church, Rocket, Users,
  Building2, TrendingUp, FileText, Award, CalendarCheck, Layers, Handshake,
};

function Icon({ name, className }) {
  const C = ICONS[name] || User;
  return <C className={className} />;
}

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative bg-[var(--trj-navy)] text-white overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={SITE.assets.boardroom}
            alt=""
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1F3A] via-[#0B1F3A]/95 to-[#0B1F3A]/55" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-24 md:pt-32 pb-24 md:pb-36">
          <div className="trj-eyebrow mb-6" data-testid="hero-eyebrow">Financial Strategy &amp; Business Solutions</div>
          <h1 className="font-serif-trj text-[44px] sm:text-6xl lg:text-7xl leading-[1.03] tracking-tight max-w-5xl">
            Financial Clarity.<br />
            <span className="text-white/85">Strategic Solutions.</span><br />
            <span className="text-[var(--trj-gold)]">Lasting Results.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-white/75 text-base md:text-lg leading-relaxed">
            Trusted tax, accounting, and business advisory services designed to help individuals,
            businesses, nonprofits, and ministries move forward with confidence.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <ScheduleConsultationButton testid="hero-schedule-consultation-btn" />
            <Link to="/contact" className="trj-btn-gold" data-testid="hero-request-review-btn">
              Request Financial Review <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Stat row */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {[
              { k: "25+", v: "Years Serving Clients" },
              { k: "4", v: "Levels of Financial Command" },
              { k: "501(c)(3)", v: "Nonprofit Specialists" },
              { k: "Year-Round", v: "Advisory Support" },
            ].map((s) => (
              <div key={s.v} className="bg-[var(--trj-navy)] px-5 py-6">
                <div className="font-serif-trj text-3xl md:text-4xl text-[var(--trj-gold)]">{s.k}</div>
                <div className="text-xs md:text-sm uppercase tracking-wider text-white/65 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="gold-rule" />
      </section>

      {/* WHO WE ARE */}
      <section className="bg-white py-20 md:py-28" data-testid="home-who-we-are">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="trj-eyebrow mb-4">Who We Are</div>
            <h2 className="font-serif-trj text-4xl md:text-5xl leading-tight text-[var(--trj-navy)] max-w-2xl">
              More Than Tax Preparation.
            </h2>
            <div className="trj-divider-gold mt-6" />
            <div className="mt-6 space-y-5 text-[15.5px] text-[var(--trj-muted)] leading-relaxed max-w-2xl">
              <p>
                TRJ Services delivers Financial Strategy &amp; Business Solutions — helping clients build,
                manage, and protect the financial systems that support long-term success.
              </p>
              <p>
                We provide tax, accounting, bookkeeping, compliance, advisory, and executive financial
                oversight services for individuals, businesses, nonprofits, and ministries.
              </p>
            </div>
            <Link to="/about" className="trj-btn-outline-navy text-sm mt-8" data-testid="who-we-are-learn-more">
              Learn About TRJ <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <img src={SITE.assets.officeModern} alt="Executive office" className="w-full h-[460px] object-cover" />
              <div className="absolute -bottom-6 -left-6 hidden md:block bg-[var(--trj-navy)] text-white px-6 py-5 border-l-2 border-[var(--trj-gold)] max-w-[260px]">
                <div className="font-serif-trj text-2xl leading-tight">&ldquo;Stronger financial systems create stronger organizations.&rdquo;</div>
                <div className="text-xs mt-2 text-[var(--trj-gold)] tracking-widest uppercase">— Tina Jacobs</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="bg-white pb-20" data-testid="home-who-we-serve">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="trj-eyebrow mb-3">Who We Help</div>
              <h2 className="font-serif-trj text-3xl md:text-4xl text-[var(--trj-navy)]">A trusted partner across every chapter of growth.</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-gray-200 border border-gray-200">
            {WHO_WE_SERVE.map((w) => (
              <div key={w.label} className="bg-white p-6 flex flex-col items-start gap-3 hover:bg-[var(--trj-light)] transition-colors">
                <div className="w-10 h-10 flex items-center justify-center bg-[var(--trj-navy)] text-[var(--trj-gold)]">
                  <Icon name={w.icon} className="w-5 h-5" />
                </div>
                <div className="font-medium text-sm text-[var(--trj-navy)] leading-snug">{w.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="bg-white py-20" data-testid="home-problem">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="trj-eyebrow mb-4">The Problem We Solve</div>
          <h2 className="font-serif-trj text-3xl md:text-5xl text-[var(--trj-navy)] leading-tight">
            Many financial problems start with <span className="italic text-[var(--trj-green)]">a lack of structure.</span>
          </h2>
          <p className="mt-6 text-[var(--trj-muted)] text-[15.5px] leading-relaxed max-w-3xl mx-auto">
            Without proper systems, organizations often face tax issues, compliance concerns, poor reporting,
            cash flow challenges, and operational confusion. Our role is to bring clarity, structure, and
            oversight to the financial side of your organization.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-5 gap-3 max-w-4xl mx-auto">
            {["Tax issues","Compliance concerns","Poor reporting","Cash flow challenges","Operational confusion"].map((p) => (
              <div key={p} className="border border-gray-200 px-4 py-3 text-sm text-[var(--trj-navy)]">
                {p}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE HELP */}
      <section className="bg-[var(--trj-light)] py-20 md:py-28" data-testid="home-how-we-help">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <div className="trj-eyebrow mb-4">Our Solutions</div>
            <h2 className="font-serif-trj text-4xl md:text-5xl text-[var(--trj-navy)] leading-tight">How We Help</h2>
            <p className="mt-5 text-[var(--trj-muted)] text-[15.5px] leading-relaxed">
              Whether you&apos;re managing personal finances, operating a business, leading a nonprofit, or
              building a new organization, TRJ Services provides the expertise, structure, and support
              needed to move forward with confidence.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s) => (
              <div key={s.slug} className="trj-card group" data-testid={`service-card-${s.slug}`}>
                <div className="w-11 h-11 bg-[var(--trj-navy)] text-[var(--trj-gold)] flex items-center justify-center">
                  <Icon name={s.icon} className="w-5 h-5" />
                </div>
                <h3 className="font-serif-trj text-2xl text-[var(--trj-navy)] mt-5 leading-snug">{s.title}</h3>
                <p className="text-[13px] text-[var(--trj-green)] mt-1 font-medium">{s.tagline}</p>
                <p className="text-sm text-[var(--trj-muted)] mt-3 leading-relaxed">{s.description}</p>
                <ul className="mt-5 space-y-1.5">
                  {s.items.map((it) => (
                    <li key={it} className="flex items-center gap-2 text-[13px] text-[var(--trj-navy)]">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[var(--trj-green)]" /> {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/solutions" className="trj-btn-outline-navy text-sm" data-testid="view-all-services-btn">
              View All Services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FOUR-LEVEL FRAMEWORK */}
      <section className="bg-[var(--trj-navy)] py-20 md:py-28 text-white relative overflow-hidden" data-testid="home-framework">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
          <div className="absolute inset-0" style={{backgroundImage:"radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",backgroundSize:"32px 32px"}} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="trj-eyebrow mb-4">The TRJ Framework</div>
            <h2 className="font-serif-trj text-4xl md:text-5xl leading-tight">The Five Levels of Financial Command</h2>
            <p className="mt-5 text-white/70 leading-relaxed max-w-2xl">
              A progressive system — from a focused diagnostic and a 72-hour rapid cleanup, through full
              cleanup, ongoing oversight, and executive financial leadership — designed to meet your
              organization exactly where it is today.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-5 gap-5">
            {FRAMEWORK_LEVELS.map((lvl) => {
              const isPremium = lvl.premium;
              return (
                <div
                  key={lvl.level}
                  data-testid={`framework-level-${lvl.level}`}
                  className={
                    isPremium
                      ? "trj-card-premium"
                      : "bg-white text-[var(--trj-navy)] p-7 border border-white/10 transition-transform hover:-translate-y-1"
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className={`text-xs tracking-[0.2em] uppercase ${isPremium ? "text-[var(--trj-gold)]" : "text-[var(--trj-green)]"} font-bold`}>
                      Level {lvl.level}
                    </div>
                    <div className={`w-9 h-9 flex items-center justify-center ${isPremium ? "bg-[var(--trj-gold)] text-[var(--trj-navy)]" : "bg-[var(--trj-navy)] text-[var(--trj-gold)]"}`}>
                      <Icon name={lvl.icon} className="w-4 h-4" />
                    </div>
                  </div>
                  {lvl.badge && (
                    <div className={`mt-3 inline-flex self-start text-[9px] uppercase tracking-widest px-2 py-0.5 ${isPremium ? "bg-[var(--trj-gold)]/20 text-[var(--trj-gold)]" : "bg-[var(--trj-gold)]/15 text-[var(--trj-gold-dark)]"}`}>
                      {lvl.badge}
                    </div>
                  )}
                  <h3 className={`font-serif-trj text-xl mt-4 leading-snug ${isPremium ? "text-white" : ""}`}>
                    {lvl.title}
                  </h3>
                  <p className={`mt-3 text-[13px] leading-relaxed ${isPremium ? "text-white/80" : "text-[var(--trj-muted)]"}`}>
                    {lvl.description}
                  </p>
                  {isPremium && (
                    <div className="mt-6 inline-flex items-center gap-2 text-xs text-[var(--trj-gold)] tracking-widest uppercase">
                      Flagship Tier
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-10">
            <Link to="/executive-financial-command" className="trj-btn-gold text-sm" data-testid="explore-efc-btn">
              Explore Financial Command™ <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY TRJ */}
      <section className="bg-white py-20 md:py-28" data-testid="home-why-trj">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="trj-eyebrow mb-4">Why TRJ Services</div>
            <h2 className="font-serif-trj text-4xl md:text-5xl leading-tight text-[var(--trj-navy)]">Why clients trust TRJ Services.</h2>
            <p className="mt-5 text-[var(--trj-muted)] leading-relaxed">
              We bring decades of cross-functional experience — tax, accounting, business strategy, nonprofit
              governance, and ministry leadership — under one trusted advisor.
            </p>
            <div className="mt-8 flex items-center gap-4">
              <img src={SITE.assets.founder} alt="Tina Jacobs" className="w-16 h-16 object-cover border border-[var(--trj-gold)]" />
              <div>
                <div className="font-serif-trj text-xl text-[var(--trj-navy)]">Tina Jacobs</div>
                <div className="text-xs uppercase tracking-widest text-[var(--trj-green)] mt-0.5">Founder</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-px bg-gray-200 border border-gray-200">
              {WHY_TRJ.map((w) => (
                <div key={w.label} className="bg-white p-5 flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-[var(--trj-light)] text-[var(--trj-green)] shrink-0">
                    <Icon name={w.icon} className="w-4 h-4" />
                  </div>
                  <div className="text-sm text-[var(--trj-navy)] font-medium leading-snug">{w.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CLIENT JOURNEY */}
      <section className="bg-[var(--trj-light)] py-20 md:py-28" data-testid="home-client-journey">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="trj-eyebrow mb-4">Client Journey</div>
            <h2 className="font-serif-trj text-4xl md:text-5xl leading-tight text-[var(--trj-navy)]">A clear path forward.</h2>
          </div>
          <ol className="mt-12 grid lg:grid-cols-5 gap-5">
            {CLIENT_JOURNEY.map((s, i) => (
              <li key={s.step} className="bg-white border border-gray-200 p-6 relative" data-testid={`journey-step-${s.step}`}>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--trj-green)] font-bold">Step 0{s.step}</div>
                <div className="font-serif-trj text-xl mt-2 text-[var(--trj-navy)]">{s.title}</div>
                <p className="text-[13px] text-[var(--trj-muted)] mt-2 leading-relaxed">{s.description}</p>
                {i < CLIENT_JOURNEY.length - 1 && (
                  <div className="hidden lg:flex absolute right-[-13px] top-1/2 -translate-y-1/2 w-6 h-6 items-center justify-center bg-[var(--trj-navy)] text-white rounded-full z-10">
                    <ArrowRight className="w-3 h-3" />
                  </div>
                )}
                <div className="lg:hidden mt-3 text-[var(--trj-green)]">
                  {i < CLIENT_JOURNEY.length - 1 && <ArrowDown className="w-4 h-4" />}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* EFC FEATURE */}
      <section
        className="bg-trj-navy-gradient relative py-24 md:py-32 text-white overflow-hidden"
        data-testid="home-efc-feature"
      >
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full" style={{background:"radial-gradient(closest-side, rgba(212,175,55,0.35), transparent)"}}/>
        </div>
        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <div className="trj-eyebrow mb-5" data-testid="efc-eyebrow">A Premium Invitation</div>
          <h2 className="font-serif-trj text-4xl md:text-6xl leading-[1.05] tracking-tight">
            Executive Financial Command™
          </h2>
          <div className="trj-divider-gold mx-auto mt-6" />
          <p className="mt-7 text-white/80 text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
            Our highest level of support for organizations seeking executive financial leadership,
            compliance oversight, and strategic direction. Designed for business owners, nonprofits,
            ministries, and leadership teams who need more than basic tax preparation.
          </p>
          <div className="mt-10 flex justify-center flex-wrap gap-3">
            <Link to="/executive-financial-command" className="trj-btn-gold" data-testid="efc-apply-btn">
              Apply Now <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="trj-btn-outline-white" data-testid="efc-learn-more-btn">
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[var(--trj-green)] py-20 md:py-24 text-white" data-testid="home-final-cta">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="font-serif-trj text-3xl md:text-5xl leading-tight">
            Stop managing financial chaos.<br/>Start building financial infrastructure.
          </h2>
          <p className="mt-6 text-white/90 max-w-3xl mx-auto leading-relaxed">
            Schedule a consultation today and discover how TRJ Services can help create the systems,
            oversight, and strategic framework your organization needs to grow with confidence.
          </p>
          <div className="mt-9 flex justify-center flex-wrap gap-3">
            <ScheduleConsultationButton variant="gold" testid="final-cta-schedule-btn" />
            <Link to="/contact" className="trj-btn-outline-white" data-testid="final-cta-request-review-btn">
              Request Financial Review
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
